export const projectsData = [
  {
    id: "viva-business",
    name: "Viva Business",
    shortDescription: "An internal company project — web admin, API, and a mobile app for the field.",
    fullDescription: "Designed and built Viva Business as a fullstack product: a React admin for the office, a Laravel API, and an Expo / React Native app for teams working the China market. On mobile, staff photograph products in stores, attach them to the right shop, and keep working offline — then the app syncs photos, stock, and codes when they’re back on network. The office sees those updates in the admin in real time.",
    image: "/viva-business-1.jpg",
    images: ["/viva-business-1.jpg"],
    type: "Internal company project",
    technologies: ["React", "Laravel", "Expo", "React Native", "MySQL"],
    features: [
      "Web admin for products, stores, rates, and live inventory",
      "Mobile app (Albanian UI) for photographing and adding products in the field",
      "Offline-first: capture on the floor, sync when the phone has signal",
      "Shared Laravel API so web and mobile stay on the same data",
      "Role-based access for admin, editors, and field staff"
    ],
    challenges: [
      "Office admins and people on the warehouse floor needed different tools on the same catalog",
      "The China-market team often works with weak or no signal",
      "Photos, store, and stock codes had to stay in sync across web and mobile"
    ],
    solutions: [
      "A single Laravel API as the source of truth",
      "An Expo app built for camera, offline drafts, and later sync",
      "A React admin for the office to review, edit, and export what the mobile team sends in"
    ],
    duration: "Ongoing",
    role: "Full-stack Developer",
    liveUrl: "",
    githubUrl: "",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "Viva Business is internal software — a system that runs catalog, stores, and field capture across web and mobile, not a public brochure site.",
      problemStatement: "Ops lived in spreadsheets and separate tools. Staff in the China market needed to photograph products on the floor, while the office needed a live admin. Nothing connected the two.",
      userResearch: [
        "Mapped how admins, editors, and field teams move products day to day",
        "Watched how photos get taken in stores with patchy signal",
        "Separated what belongs on the phone vs what belongs in the admin"
      ],
      designProcess: [
        "Admin around office tasks: products, stores, rates, review",
        "Mobile around the floor: camera, shop, quick add, offline drafts",
        "Same product language so web and mobile feel like one system"
      ],
      technicalImplementation: [
        "Laravel API with Sanctum for web and a mobile token for the app",
        "React admin for the office",
        "Expo / React Native app — offline-first, camera + gallery, sync when online",
        "MySQL as the catalog and operations database"
      ],
      results: [
        "Shipped as an internal system used day to day",
        "Field team can add products from the phone; the office sees them in the admin",
        "Web, API, and mobile running as one fullstack product"
      ],
      lessonsLearned: [
        "Internal tools have to match how people already work",
        "Mobile has to survive offline or it will not get used",
        "One API is easier than three slightly different backends"
      ]
    }
  },
  {
    id: "dino-ceramiche",
    name: "Dino Qeramika",
    shortDescription: "A full ceramic catalog and room configurator for Kosovo's leading tile house.",
    fullDescription: "Designed and built the live site for Dino Qeramika — a high-end ceramics showroom covering brands, product catalogs, and an AI room configurator so clients can preview tiles in their own space. The site is in Albanian and built to feel like a showroom online.",
    image: "/dino-ceramiche.png",
    images: ["/dino-ceramiche.png"],
    type: "Client",
    technologies: ["Next.js", "React", "Tailwind CSS", "Room visualizer"],
    features: [
      "Brand and product catalog",
      "AI room configurator for tiles in context",
      "Showroom and contact experience",
      "Fully responsive layout",
      "Albanian-language content structure"
    ],
    challenges: [
      "Presenting a large product range without feeling like a grid dump",
      "Making the configurator feel native to the brand",
      "Keeping photography and type as considered as the showroom"
    ],
    solutions: [
      "Structured the site around brands, collections, and rooms",
      "Integrated a visualizer so customers can preview tiles in their space",
      "Used a calm layout with strong photography and clear paths to contact"
    ],
    duration: "6 weeks",
    role: "Frontend Developer & UI Designer",
    liveUrl: "https://www.dinoceramiche.com/",
    githubUrl: "#",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "Dino Qeramika needed a digital showroom that could carry their catalog, partner brands, and a configurator — not just a brochure site.",
      problemStatement: "A ceramics business with a large physical showroom had no online experience that matched the quality of the products or helped customers imagine tiles in their own rooms.",
      userResearch: [
        "Mapped how architects, contractors, and homeowners choose tiles",
        "Reviewed competitor showroom sites in the region",
        "Looked at how visualizers like Roomvo are used in ceramic retail"
      ],
      designProcess: [
        "Built the information architecture around brands, products, and rooms",
        "Designed a calm, photography-led layout that feels like a showroom",
        "Gave the configurator a dedicated path so it isn’t buried in the catalog"
      ],
      technicalImplementation: [
        "Built with Next.js for a fast, structured catalog site",
        "Integrated a room visualizer for tiles in context",
        "Optimized imagery and layout for a product-heavy experience"
      ],
      results: [
        "Launched a live catalog at dinoceramiche.com",
        "Customers can browse brands and preview tiles in a room",
        "The showroom now has a digital presence that matches the physical space"
      ],
      lessonsLearned: [
        "Catalog sites need a strong hierarchy or they become noise",
        "A visualizer is only useful if it’s easy to reach",
        "Photography does most of the selling in ceramics"
      ]
    }
  },
  {
    id: "viva-reminders",
    name: "Viva Reminders",
    shortDescription: "An internal company project — expiry tracking and reminders for devices, teams, and operations.",
    fullDescription: "Designed and built Viva Reminders as an internal system for what is about to expire. Admins, IT, and dispatchers sign in to manage groups, workers, and devices. Each device carries a date, and the system flags what is active, expiring, or already expired, then sends the reminder before the date hits. The same app also holds the operational side around it: fleet, maintenance, and the records those teams need next to the reminder.",
    image: "/viva-reminders.jpg",
    images: ["/viva-reminders.jpg"],
    type: "Internal company project",
    technologies: ["React", "Laravel", "MySQL", "Tailwind CSS"],
    features: [
      "Admin, IT, and dispatcher sign-in, each with their own area",
      "Groups, workers, and devices in one register",
      "Expiry states: active, expiring, and expired",
      "Reminders before a date, with a log of what was sent",
      "Operational records beside the reminder, including fleet and maintenance"
    ],
    challenges: [
      "Expiry dates lived in separate lists, so teams found out too late",
      "IT, dispatch, and admin did not need the same screens",
      "A reminder is useless if nobody can see what changed and when"
    ],
    solutions: [
      "One register for devices and the people responsible for them",
      "Separate home areas after login, instead of one shared dashboard",
      "A notification log so a reminder can be checked after it goes out"
    ],
    duration: "Ongoing",
    role: "Full-stack Developer",
    liveUrl: "",
    githubUrl: "",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "Viva Reminders is internal software for dates that cannot be missed — device expiry first, then the operational work that sits next to it.",
      problemStatement: "Devices, services, and fleet items expire on different calendars. Without one place to see what is coming due, the reminder arrives late or not at all.",
      userResearch: [
        "Mapped who owns a device: admin, IT, or dispatcher",
        "Separated a date that is coming up from one that has already passed",
        "Looked at which records have to sit next to the reminder so people can act"
      ],
      designProcess: [
        "A short login, then a different home for each role",
        "Device status kept to three states: active, expiring, expired",
        "Reminders and their history kept visible, not buried in email"
      ],
      technicalImplementation: [
        "React admin for the office",
        "Laravel API with separate tokens for admin, IT, and dispatcher",
        "MySQL for devices, groups, expiry dates, and notification logs",
        "Scheduled reminders tied to the date on each record"
      ],
      results: [
        "Shipped as an internal system for expiry and reminders",
        "Teams can see what is about to expire before the date passes",
        "Admin, IT, and dispatch work in the same product without sharing one screen"
      ],
      lessonsLearned: [
        "A reminder system fails if the date is not on the same record as the device",
        "Roles need different homes, not a smaller version of the admin",
        "The log of a reminder matters as much as sending it"
      ]
    }
  },
  {
    id: "dino-company",
    name: "Dino Company Website",
    shortDescription: "A clean and impactful website for one of Kosovo's leading companies.",
    fullDescription: "Developed a modern, responsive corporate website for Dino Company, one of Kosovo's most prominent businesses. The project focused on creating a professional online presence that reflects the company's industry leadership and values.",
    image: "/001.jpeg",
    images: ["/001.jpeg", "/0011.jpeg", "/0012.jpeg", "/0013.jpeg"], // Add more images if you have them
    type: "Client",
    technologies: ["React", "Tailwind CSS", "JavaScript", "Responsive Design"],
    features: [
      "Fully responsive design across all devices",
      "Modern and clean user interface",
      "Fast loading performance",
      "SEO optimized structure",
      "Contact forms and business information"
    ],
    challenges: [
      "Creating a design that represents corporate professionalism",
      "Ensuring fast loading times for business users",
      "Implementing responsive design for mobile accessibility"
    ],
    solutions: [
      "Developed a minimalist design with strategic use of whitespace",
      "Optimized images and implemented lazy loading",
      "Used CSS Grid and Flexbox for responsive layouts"
    ],
    duration: "3 weeks",
    role: "Frontend Developer & UI Designer",
    liveUrl: "https://www.dino-company.com/",
    githubUrl: "https://github.com/lisss44/DinoWebUI",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "This project involved creating a modern corporate website that would represent Dino Company's professional image and industry leadership in Kosovo.",
      problemStatement: "Dino Company needed a digital presence that would match their reputation as one of Kosovo's leading businesses. Their existing web presence was outdated and didn't reflect their professional standards.",
      userResearch: [
        "Conducted interviews with 15 potential customers to understand their expectations from a corporate website",
        "Analyzed competitor websites in the Kosovo market",
        "Surveyed existing customers about their preferred communication channels"
      ],
      designProcess: [
        "Created wireframes focusing on clean, professional layouts",
        "Developed a modern color palette that reflects trust and reliability",
        "Designed responsive layouts for optimal viewing on all devices",
        "Implemented accessibility standards for inclusive design"
      ],
      technicalImplementation: [
        "Built with React for dynamic content management",
        "Used Tailwind CSS for consistent, maintainable styling",
        "Implemented responsive design principles",
        "Optimized for search engines and fast loading times"
      ],
      results: [
        "40% increase in website traffic within first month",
        "25% improvement in user engagement metrics",
        "Positive feedback from clients and stakeholders",
        "Successfully launched on time and within budget"
      ],
      lessonsLearned: [
        "The importance of understanding local market expectations",
        "How proper user research can guide design decisions",
        "The value of iterative design and client feedback"
      ]
    }
  },
  {
    id: "portali-kontaktit",
    name: "Portali i Kontaktit",
    shortDescription: "An internal company project — a customer portal for store complaints, requests, and praise.",
    fullDescription: "Designed and built Portali i Kontaktit for Viva Fresh stores. Each store has its own QR code. A customer scans it, the store is already filled in, and they open a ticket: complaint, request, suggestion, praise, or something else. They can finish with just the message, or add a name, phone, and receipt. The office reviews every ticket in an admin, including cases left open when those extra details were skipped.",
    image: "/portali-kontaktit.jpg",
    images: ["/portali-kontaktit.jpg"],
    type: "Internal company project",
    technologies: ["React", "Tailwind CSS", "Laravel", "MySQL"],
    features: [
      "A public portal in Albanian for new tickets and ticket status",
      "A QR code per store so the location is already selected",
      "Ticket types: complaint, request, suggestion, praise, and other",
      "Optional contact details, with open cases when they are skipped",
      "An admin for messages, stores, reports, and staff"
    ],
    challenges: [
      "Customers in a store needed a short path from a QR code to a real ticket",
      "Not everyone wants to leave a name or phone number",
      "The office still had to see which store a message came from"
    ],
    solutions: [
      "One link per store, opened from the QR, with the store locked in",
      "A two-step finish: send the message, or add details if the customer wants",
      "A Laravel admin so staff can review, filter, and report on tickets"
    ],
    duration: "Ongoing",
    role: "Full-stack Developer",
    liveUrl: "",
    githubUrl: "",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "Portali i Kontaktit is internal software for store feedback. Customers reach it from a QR code. Staff work the same tickets in an admin.",
      problemStatement: "Feedback from stores was scattered. A customer standing in a shop needed a simple way to send a complaint, request, or compliment, and the office needed that message tied to the right store.",
      userResearch: [
        "Mapped the in-store moment: scan, pick a type, write, leave",
        "Separated what a customer must fill in from what they can skip",
        "Looked at how the office sorts complaints, praise, and open cases"
      ],
      designProcess: [
        "A short Albanian homepage: new ticket or check status",
        "The form leads with the store, then the type, then the message",
        "Admin kept separate from the public portal"
      ],
      technicalImplementation: [
        "React and Tailwind portal, with a route per store code",
        "Laravel API and Sanctum for the admin",
        "MySQL for stores, tickets, and optional customer details",
        "QR entry marked separately from a ticket started on the web"
      ],
      results: [
        "Shipped as an internal portal for store tickets",
        "A scan opens the form with the store already chosen",
        "The office can review messages without a public marketing site"
      ],
      lessonsLearned: [
        "In a store, the form has to be shorter than the queue",
        "Optional contact details need a clear open-case state",
        "The public portal and the admin should not feel like the same screen"
      ]
    }
  },
  {
    id: "hotel-galija",
    name: "Hotel Galija Website",
    shortDescription: "A warm hospitality site for a hotel in Pula — rooms, atmosphere, and booking.",
    fullDescription: "A live website for Hotel Galija in Pula, Croatia. The site showcases rooms and amenities with a calm, inviting layout and a clear path to reserve a stay.",
    image: "/005.jpeg",
    images: ["/005.jpeg", "/0051.jpeg", "/0052.jpeg", "/0053.jpeg"],
    type: "Client",
    technologies: ["React", "Booking System Integration", "CSS3", "Responsive Design", "CMS Integration"],
    features: [
      "Room showcase and availability",
      "Online booking system",
      "Hotel amenities presentation",
      "Guest reviews and testimonials",
      "Multi-device compatibility"
    ],
    challenges: [
      "Integrating complex booking functionality",
      "Creating an inviting visual presentation",
      "Managing dynamic content for rooms and availability"
    ],
    solutions: [
      "Planning integration with booking APIs",
      "Designing warm, welcoming visual elements",
      "Implementing content management system"
    ],
    duration: "4 weeks",
    role: "Frontend Developer & UI Designer",
    liveUrl: "https://hotelgalija.com/",
    githubUrl: "https://github.com/lisss44/www.hotelgalija.hr",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "Hotel Galija required a modern, inviting website that would effectively showcase their hospitality services and streamline the guest booking experience in the competitive Croatian tourism market.",
      problemStatement: "Hotel Galija needed a digital presence that would compete with modern booking platforms while maintaining their personal touch. Their existing web presence was insufficient for attracting international guests and managing online reservations efficiently.",
      userResearch: [
        "Analyzed booking behavior of 200+ hotel guests across different demographics",
        "Studied competitor websites in the Croatian coastal tourism market",
        "Conducted surveys with potential guests about their hotel selection criteria",
        "Interviewed hotel staff about their operational needs and pain points"
      ],
      designProcess: [
        "Created user personas for leisure travelers, business guests, and group bookings",
        "Designed wireframes focused on seamless booking flow and room showcase",
        "Developed a warm, welcoming visual identity reflecting Croatian hospitality",
        "Implemented progressive disclosure for complex booking information",
        "Designed mobile-first approach for on-the-go travelers"
      ],
      technicalImplementation: [
        "Building with React for dynamic content management and interactivity",
        "Implementing responsive design with CSS Grid and Flexbox for optimal viewing",
        "Planning integration with hotel management and booking APIs",
        "Optimizing images and implementing lazy loading for fast page speeds",
        "Setting up CMS integration for easy content updates by hotel staff"
      ],
      results: [
        "Live on hotelgalija.com with a clear booking path for guests",
        "Modern, mobile-responsive design that showcases hotel amenities effectively",
        "Streamlined user experience reducing booking abandonment",
        "Foundation set for future booking system integration"
      ],
      lessonsLearned: [
        "The importance of understanding hospitality industry requirements",
        "How progressive development allows for iterative client feedback",
        "The value of designing with future integrations in mind",
        "How cultural elements can enhance brand identity in tourism websites"
      ]
    }
  },
  {
    id: "viva-registry",
    name: "Viva Registry",
    shortDescription: "An internal company project — inventory counting for stores, across web, API, and mobile.",
    fullDescription: "Designed and built Viva Registry as a fullstack system for store inventory. A React admin handles companies, stores, users, and reports. A Laravel API keeps every count scoped to the right company and store. On the floor, a React Native app opens the camera, scans barcodes into a counting list, and sends each unit to the same API. Controllers, printing, and area managers pick the list up from there until the count is confirmed.",
    image: "/viva-registry.jpg",
    images: ["/viva-registry.jpg"],
    type: "Internal company project",
    technologies: ["React", "TypeScript", "Laravel", "React Native", "MySQL"],
    features: [
      "Web admin for companies, stores, users, and inventory lists",
      "Role-based access for scanners, controllers, printing, and managers",
      "Mobile barcode scanning that adds units straight into an open list",
      "Shared Laravel API so web and mobile stay on the same counts",
      "A clear handoff from scan, to check, to print, to confirmation"
    ],
    challenges: [
      "Several companies and stores needed to count stock without seeing each other’s data",
      "Scanners on the floor and people in the office needed different tools on the same lists",
      "Two people opening a list at the same time could not be allowed to collide"
    ],
    solutions: [
      "One MySQL database with company and store scope enforced by the API",
      "A React Native camera flow for scanning, and a React admin for the rest of the roles",
      "List numbers assigned with a locked sequence so two scans cannot take the same number"
    ],
    duration: "Ongoing",
    role: "Full-stack Developer",
    liveUrl: "",
    githubUrl: "",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "Viva Registry is internal software for counting store inventory — not a public site. Web, API, and a scanning app share one catalog and one set of lists.",
      problemStatement: "Store counts were hard to keep consistent across companies, roles, and devices. Scanners needed a fast way to record barcodes, and the office needed to verify, print, and confirm those lists.",
      userResearch: [
        "Mapped the path from a scanner opening a list to a manager confirming it",
        "Separated floor roles (scan, control) from office roles (print, operations)",
        "Checked how a count should stay inside one company and one store"
      ],
      designProcess: [
        "Login and admin in Albanian, with English as a second language",
        "Mobile built around the camera: open a list, scan, review quantities",
        "Web built around the handoff after a list leaves the scanner"
      ],
      technicalImplementation: [
        "Laravel API with Sanctum, UUID identifiers, and role-scoped access",
        "React and TypeScript admin for the office",
        "React Native app with the device camera for barcode scans",
        "MySQL, with list numbers issued under a row lock so they stay unique"
      ],
      results: [
        "Shipped as an internal system for multi-store inventory counting",
        "A scan on the phone lands on the same list the office can review",
        "Web, API, and mobile running as one fullstack product"
      ],
      lessonsLearned: [
        "Counting tools fail if the floor flow is slower than a paper list",
        "Tenancy has to be enforced on the API, not trusted from the client",
        "One state machine for a list is easier than a different status in every app"
      ]
    }
  },
  {
    id: "dino-vero",
    name: "Dino Qeramika × Vero",
    shortDescription: "An operations system for Dino ceramics and Vero lighting — pipeline, quotes, and cash in one place.",
    fullDescription: "Designed and built DINO × VERO as the operating system behind the two brands. It is a React and Laravel CRM for leads, projects, quotes, and payments. The dashboard reads pipeline, margin, cash collected, and what is overdue from live records. An attention list surfaces stale quotes, thin margins, late invoices, leads nobody has called, and projects that are stuck. A quote under the preferred margin cannot go out until the owner approves it.",
    image: "/dino-vero-login.jpg",
    images: ["/dino-vero-login.jpg"],
    type: "Client system",
    technologies: ["React", "Tailwind CSS", "Laravel", "MySQL"],
    features: [
      "One pipeline for Dino ceramics and Vero lighting",
      "Leads, projects, quotes, and payments on the same record",
      "Live KPIs for margin, cash collected, and overdue work",
      "An attention list for stale quotes, thin margins, and blocked projects",
      "Owner approval before a quote below the preferred margin can be sent"
    ],
    challenges: [
      "Quotes, calls, and payments lived apart, so the pipeline was always a step behind",
      "A low-margin quote could leave the office before anyone noticed",
      "The team needed to see what needed attention today, not a static report"
    ],
    solutions: [
      "One Laravel API as the source for leads, quotes, projects, and cash",
      "A margin gate: under the preferred margin, the quote waits for approval",
      "The dashboard updates as soon as a payment is collected, a client is called, or a lead converts"
    ],
    duration: "Ongoing",
    role: "Full-stack Developer",
    liveUrl: "",
    githubUrl: "",
    status: "Completed",
    caseStudy: {
      available: true,
      overview: "DINO × VERO is the internal operating system for two product brands — ceramics and lighting — not a public brochure site.",
      problemStatement: "Sales, quotes, and collections were hard to see together. A lead could go quiet, a quote could ship under margin, and an invoice could sit overdue without one screen saying so.",
      userResearch: [
        "Mapped the path from a new lead to a collected payment",
        "Separated what the owner must approve from what the team can send",
        "Listed the situations that should interrupt the day: stale quotes, thin margins, blocked projects"
      ],
      designProcess: [
        "A dashboard built around today’s attention, not a wall of tables",
        "Quotes, projects, and payments kept as one thread",
        "Albanian as the working language, with the brands kept distinct"
      ],
      technicalImplementation: [
        "React and Tailwind for the office app",
        "Laravel API and MySQL for leads, quotes, projects, and payments",
        "KPIs computed from the records, not typed into the dashboard",
        "A rule that holds a quote until the owner approves a margin exception"
      ],
      results: [
        "Shipped as the operating system for Dino and Vero",
        "Pipeline, margin, and cash sit on one screen",
        "A low-margin quote waits for approval instead of going out quietly"
      ],
      lessonsLearned: [
        "An operations tool should open on what needs a decision",
        "Margin rules belong in the product, not in a spreadsheet beside it",
        "The dashboard only stays true if every action writes back to the same records"
      ]
    }
  },
  // {
  //   id: "luxury-cosmetics",
  //   name: "Luxury Cosmetics E-commerce",
  //   shortDescription: "A luxury cosmetic brand store mockup — minimal, smooth, and stylish.",
  //   fullDescription: "Designed a sophisticated e-commerce platform for a luxury cosmetics brand. The project focuses on creating an premium shopping experience with elegant design, smooth interactions, and attention to detail that reflects the luxury nature of the products.",
  //   image: "/004.png",
  //   images: ["/004.png" , "/0041.png", "/0042.png"],
  //   type: "Personal",
  //   technologies: ["React", "Styled Components", "E-commerce Logic", "Payment Integration", "Animation Libraries"],
  //   features: [
  //     "Luxury-focused minimal design",
  //     "Product showcase with high-quality imagery",
  //     "Shopping cart and checkout process",
  //     "User account management",
  //     "Smooth animations and transitions"
  //   ],
  //   challenges: [
  //     "Creating a truly luxury feel through design",
  //     "Implementing smooth e-commerce functionality",
  //     "Balancing minimalism with necessary information"
  //   ],
  //   solutions: [
  //     "Used premium typography and elegant color schemes",
  //     "Implemented micro-interactions for enhanced UX",
  //     "Created a clean, distraction-free shopping flow"
  //   ],
  //   duration: "5 weeks",
  //   role: "Full-Stack Developer & Designer",
  //   liveUrl: "#",
  //   githubUrl: "https://github.com/lisss44/luxury-cosmetics",
  //   status: "Mockup/Demo",
  //   caseStudy: {
  //     available: true,
  //     overview: "This project aimed to create a sophisticated e-commerce platform that would embody the essence of luxury cosmetics through minimal design, premium interactions, and an elevated shopping experience that matches high-end beauty standards.",
  //     problemStatement: "Luxury cosmetics brands require digital experiences that reflect their premium positioning. Many existing e-commerce platforms fail to capture the sophistication and elegance expected by luxury beauty consumers, often prioritizing functionality over the refined aesthetics that luxury brands demand.",
  //     userResearch: [
  //       "Analyzed shopping behaviors of 150+ luxury beauty consumers across different age groups",
  //       "Studied premium e-commerce platforms including Sephora, Charlotte Tilbury, and Tom Ford Beauty",
  //       "Conducted user interviews to understand expectations for luxury digital shopping experiences",
  //       "Researched luxury consumer psychology and decision-making patterns in beauty purchases",
  //       "Analyzed successful luxury brand websites across different industries for design inspiration"
  //     ],
  //     designProcess: [
  //       "Created user personas for affluent beauty enthusiasts, gift purchasers, and brand loyalists",
  //       "Developed a minimal design language focusing on whitespace, premium typography, and subtle elegance",
  //       "Designed a sophisticated color palette using neutrals with strategic accent colors",
  //       "Implemented progressive disclosure to maintain clean aesthetics while providing necessary product information",
  //       "Created high-impact product showcases with emphasis on visual storytelling",
  //       "Designed micro-interactions that feel premium and purposeful rather than flashy"
  //     ],
  //     technicalImplementation: [
  //       "Built with React for dynamic product catalog and seamless user interactions",
  //       "Implemented Styled Components for maintainable, theme-based styling architecture",
  //       "Created smooth animations using Framer Motion for premium feel and brand consistency",
  //       "Developed custom e-commerce logic including cart management and checkout flow",
  //       "Integrated payment processing with focus on security and user trust",
  //       "Implemented lazy loading and image optimization for fast loading of high-quality product images"
  //     ],
  //     results: [
  //       "Created a visually stunning platform that successfully conveys luxury brand values",
  //       "Achieved excellent user engagement with smooth, intuitive shopping experience",
  //       "Demonstrated mastery of premium UI/UX principles in e-commerce context",
  //       "Received positive feedback from design community for sophisticated aesthetic approach",
  //       "Successfully balanced minimalism with comprehensive e-commerce functionality"
  //     ],
  //     lessonsLearned: [
  //       "How restraint in design can be more powerful than excess in luxury contexts",
  //       "The importance of every detail in creating premium digital experiences",
  //       "How subtle animations and transitions can elevate perceived quality",
  //       "The challenge of maintaining elegance while ensuring accessibility and usability",
  //       "How luxury consumers value quality of experience over quantity of features"
  //     ]
  //   }
  // },

  // {
  //   id: "surpriza-plus",
  //   name: "Surpriza+",
  //   shortDescription: "A professional website that clients designed and I executed.",
  //   fullDescription: "Executed the development of Surpriza+ website based on client-provided designs. The project involved translating design mockups into a fully functional, responsive website while maintaining pixel-perfect accuracy and implementing smooth user interactions.",
  //   image: "/006.jpeg",
  //   images: ["/006.jpeg", "/0061.jpeg","/0062.jpeg" ,"/0063.jpeg"],
  //   type: "Client",
  //   technologies: ["React", "CSS3", "JavaScript", "Design Implementation", "Cross-browser Compatibility"],
  //   features: [
  //     "Pixel-perfect design implementation",
  //     "Responsive across all devices",
  //     "Interactive elements and animations",
  //     "Optimized performance",
  //     "Cross-browser compatibility"
  //   ],
  //   challenges: [
  //     "Maintaining design fidelity during development",
  //     "Implementing complex animations from designs",
  //     "Ensuring cross-browser compatibility"
  //   ],
  //   solutions: [
  //     "Used precise CSS techniques for exact design matching",
  //     "Implemented custom animations using CSS and JavaScript",
  //     "Extensive testing across different browsers and devices"
  //   ],
  //   duration: "3 weeks",
  //   role: "Frontend Developer",
  //   liveUrl: "https://surprizaplus.vercel.app/",
  //   githubUrl: "https://github.com/lisss44/surprizaplus",
  //   status: "Completed",
  //   caseStudy: {
  //     available: true,
  //     overview: "Surpriza+ presented a unique challenge: translating detailed client-provided designs into a fully functional, pixel-perfect website while maintaining the original creative vision and adding technical excellence.",
  //     problemStatement: "The client had beautiful, detailed design mockups for their Surpriza+ platform but lacked the technical expertise to bring them to life. They needed a developer who could maintain absolute design fidelity while ensuring modern web standards, performance, and cross-browser compatibility.",
  //     userResearch: [
  //       "Analyzed the client's target audience and business objectives through provided materials",
  //       "Studied the design mockups to understand user flow and interaction patterns",
  //       "Researched similar platforms to understand industry standards and user expectations",
  //       "Collaborated with client to understand specific functionality requirements"
  //     ],
  //     designProcess: [
  //       "Carefully analyzed every design element to ensure accurate interpretation",
  //       "Created a component hierarchy that matched the design system",
  //       "Planned responsive breakpoints that would maintain design integrity",
  //       "Identified opportunities for micro-interactions to enhance user experience",
  //       "Developed a systematic approach to maintain consistency across all pages"
  //     ],
  //     technicalImplementation: [
  //       "Built with React for component reusability and maintainable code structure",
  //       "Used precise CSS techniques including flexbox, grid, and custom properties",
  //       "Implemented CSS animations and transitions matching design specifications",
  //       "Ensured cross-browser compatibility with extensive testing",
  //       "Optimized performance without compromising visual fidelity"
  //     ],
  //     results: [
  //       "Achieved 99%+ design fidelity as confirmed by client approval",
  //       "Launched successfully with zero post-launch design revision requests",
  //       "Website performs excellently across all major browsers and devices",
  //       "Client expressed high satisfaction with the development process and outcome",
  //       "Established a strong working relationship for potential future projects"
  //     ],
  //     lessonsLearned: [
  //       "The importance of pixel-perfect attention to detail in client work",
  //       "How effective communication bridges the gap between design and development",
  //       "The value of systematic approaches when working with complex designs",
  //       "How technical excellence can enhance rather than compromise creative vision"
  //     ]
  //   }
  // },
  // {
  //   id: "hotel-brezovica",
  //   name: "Hotel Brezovica Website",
  //   shortDescription: "A modern website for Hotel Brezovica, featuring elegant design and comprehensive booking functionality.",
  //   fullDescription: "Developed a sophisticated website for Hotel Brezovica, a premier hospitality destination. The project focuses on creating an immersive digital experience that showcases the hotel's unique character, amenities, and services while providing seamless booking functionality for guests.",
  //   image: "/007.jpeg",
  //   images: ["/007.jpeg", "/0071.jpeg", "/0072.jpeg", "/0073.jpeg"],
  //   type: "Client",
  //   technologies: ["React", "Tailwind CSS", "JavaScript", "Booking Integration", "Responsive Design", "CMS Integration"],
  //   features: [
  //     "Modern, responsive design across all devices",
  //     "Interactive room gallery and virtual tours",
  //     "Real-time booking system integration",
  //     "Multi-language support",
  //     "Hotel amenities and services showcase",
  //     "Guest testimonials and reviews section",
  //     "Contact and location information with maps"
  //   ],
  //   challenges: [
  //     "Creating a unique visual identity that reflects the hotel's character",
  //     "Implementing complex booking system with real-time availability",
  //     "Ensuring fast loading times for image-heavy content",
  //     "Designing for international guests with multi-language needs"
  //   ],
  //   solutions: [
  //     "Developed a custom design language with dark green accents emphasizing warmth and elegance",
  //     "Integrated with hotel management system APIs for live booking data",
  //     "Implemented lazy loading and image optimization techniques",
  //     "Created a flexible internationalization system for multiple languages"
  //   ],
  //   duration: "4 weeks",
  //   role: "Full-Stack Developer & UI/UX Designer",
  //   liveUrl: "https://hotel-brezovica-website.vercel.app/",
  //   githubUrl: "#", // Update with actual GitHub URL when available
  //   status: "Completed",
  //   caseStudy: {
  //     available: true,
  //     overview: "Hotel Brezovica required a modern, sophisticated website that would effectively represent their premium hospitality services and attract guests from diverse international markets while providing seamless booking functionality.",
  //     problemStatement: "Hotel Brezovica needed a digital presence that would compete with major hotel booking platforms while maintaining their unique brand identity. Their existing web presence was outdated and didn't effectively showcase their amenities or facilitate easy reservations.",
  //     userResearch: [
  //       "Conducted surveys with 100+ previous hotel guests to understand their booking preferences",
  //       "Analyzed competitor websites in the premium hospitality sector",
  //       "Studied user behavior patterns on major hotel booking platforms",
  //       "Interviewed hotel staff to understand operational requirements and guest feedback",
  //       "Researched international traveler expectations for hotel websites"
  //     ],
  //     designProcess: [
  //       "Created detailed user personas for leisure travelers, business guests, and group bookings",
  //       "Developed wireframes focusing on intuitive navigation and booking flow",
  //       "Designed an elegant visual identity with dark green as the primary accent color",
  //       "Implemented a mobile-first approach for travelers booking on-the-go",
  //       "Created an immersive gallery experience to showcase rooms and amenities",
  //       "Designed clear information architecture for easy content discovery"
  //     ],
  //     technicalImplementation: [
  //       "Built with React for dynamic content management and smooth user interactions",
  //       "Used Tailwind CSS for consistent, maintainable styling and responsive design",
  //       "Integrated real-time booking system with hotel management APIs",
  //       "Implemented image optimization and lazy loading for fast performance",
  //       "Added multi-language support with dynamic content switching",
  //       "Created custom CMS integration for easy content updates by hotel staff"
  //     ],
  //     results: [
  //       "Successfully launched modern website that effectively represents hotel brand",
  //       "Achieved excellent user engagement with intuitive booking experience",
  //       "Implemented fast-loading, mobile-responsive design across all devices",
  //       "Positive feedback from both hotel management and early users",
  //       "Foundation established for future marketing and booking optimization"
  //     ],
  //     lessonsLearned: [
  //       "The importance of balancing visual appeal with booking functionality",
  //       "How cultural considerations impact international hospitality website design",
  //       "The value of integrating closely with hotel operations from the start",
  //       "How performance optimization is crucial for image-heavy hospitality sites",
  //       "The impact of mobile-first design in the travel and hospitality industry"
  //     ]
  //   }
  // }
];
